import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
  Button,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Inventory as InventoryIcon,
  Description as DescriptionIcon,
  Folder as FolderIcon,
  Logout as LogoutIcon,
  Business as BusinessIcon,
  AdminPanelSettings as AdminIcon,
  ExpandLess,
  ExpandMore,
  Architecture as ArchitectureIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useSnackbar } from 'notistack';

const drawerWidth = 240;

const AppHeader: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [documentsMenuOpen, setDocumentsMenuOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    enqueueSnackbar('로그아웃되었습니다.', { variant: 'success' });
    navigate('/login');
  };

  const menuItems = [
    { text: '재고관리', icon: <InventoryIcon />, path: '/inventory' },
    { text: '자재관리', icon: <InventoryIcon />, path: '/materials' },
    { text: '설계관리', icon: <ArchitectureIcon />, path: '/designs' },
    { text: '문서관리', icon: <FolderIcon />, path: '/documents', 
      subItems: [
        { text: '매출 상세', icon: <DescriptionIcon />, path: '/documents/sales' },
        { text: '매출 계획', icon: <DescriptionIcon />, path: '/documents/planning' },
        { text: '생산 문서', icon: <DescriptionIcon />, path: '/documents/production' },
        { text: '간트 차트', icon: <DescriptionIcon />, path: '/documents/schedule' },
        { text: '기성 청구서', icon: <DescriptionIcon />, path: '/documents/billing' }

      ]
    },
    { text: '매출관리', icon: <DescriptionIcon />, path: '/sales/list' },
    { text: '생산관리', icon: <InventoryIcon />, path: '/production' },
    { text: '마이페이지', icon: <PersonIcon />, path: '/mypage' },
  ];

  const adminMenuItems = [
    { text: '매출 등록', icon: <DescriptionIcon />, path: '/sales/register' },
    { text: '업체 등록', icon: <BusinessIcon />, path: '/company/register' },
    { text: '회원 등록', icon: <BusinessIcon />, path: '/users/register' },
  ];

  const documentMenuItems = [
    {
      text: '매출 문서',
      icon: <DescriptionIcon />,
      path: '/documents/sales',
      subItems: [
        { text: '매출 계획', icon: <DescriptionIcon />, path: '/documents/planning' },
        { text: '생산 문서', icon: <DescriptionIcon />, path: '/documents/production' },
      ]
    },
  ];

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          관리 시스템
        </Typography>
      </Toolbar>
      <List>
        {menuItems.map((item) => (
          <React.Fragment key={item.text}>
            <ListItemButton
              onClick={() => {
                if (item.subItems) {
                  setDocumentsMenuOpen(!documentsMenuOpen);
                } else {
                  navigate(item.path);
                  if (isMobile) {
                    setMobileOpen(false);
                  }
                }
              }}
              selected={!item.subItems && location.pathname === item.path}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
              {item.subItems && (documentsMenuOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
            {item.subItems && (
              <Collapse in={documentsMenuOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subItems.map((subItem) => (
                    <ListItemButton
                      key={subItem.text}
                      onClick={() => {
                        navigate(subItem.path);
                        if (isMobile) {
                          setMobileOpen(false);
                        }
                      }}
                      selected={location.pathname === subItem.path}
                      sx={{ pl: 4 }}
                    >
                      <ListItemIcon>{subItem.icon}</ListItemIcon>
                      <ListItemText primary={subItem.text} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
        <ListItemButton
          onClick={() => {
            setAdminMenuOpen(!adminMenuOpen);
            if (isMobile) {
              setMobileOpen(false);
            }
          }}
        >
          <ListItemIcon>
            <AdminIcon />
          </ListItemIcon>
          <ListItemText primary="관리자" />
          {adminMenuOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={adminMenuOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {adminMenuItems.map((item) => (
              <ListItemButton
                key={item.text}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) {
                    setMobileOpen(false);
                  }
                }}
                selected={location.pathname === item.path}
                sx={{ pl: 4 }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find(item => item.path === location.pathname)?.text || 
             adminMenuItems.find(item => item.path === location.pathname)?.text || 
             '관리 시스템'}
          </Typography>
          <Button
            color="inherit"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
          >
            로그아웃
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
          >
            {drawer}
          </Drawer>
        ) : (
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
            open
          >
            {drawer}
          </Drawer>
        )}
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default AppHeader;
